import React, { useMemo } from "react";
import { FaDiscord, FaGlobe, FaSteam } from "react-icons/fa";
import { AiFillInstagram } from "react-icons/ai";
import {
    DISCORD_URL,
    INSTAGRAM_URL,
    STEAM_JOURNEY_TO_ECHO,
    WEBSITE_URL,
} from "../../constants/links";
import { useSiteNavigation } from "../../hooks/useSiteNavigation";

const InstagramIcon = AiFillInstagram as unknown as React.FC<{ className?: string }>;
const DiscordIcon = FaDiscord as unknown as React.FC<{ className?: string }>;
const SteamIcon = FaSteam as unknown as React.FC<{ className?: string }>;
const GlobeIcon = FaGlobe as unknown as React.FC<{ className?: string }>;

const FooterComponent: React.FC = () => {
    const year = new Date().getFullYear();
    const { goToPathTop, scrollToId } = useSiteNavigation();

    const linkGroups = useMemo(
        () => [
            {
                title: "Navigation",
                links: [
                    { label: "Home", action: () => goToPathTop("/") },
                    { label: "Games", action: () => goToPathTop("/games") },
                    { label: "About", action: () => scrollToId("about") },
                ],
            },
            {
                title: "Projects",
                links: [
                    { label: "Journey to Echo", action: () => scrollToId("northruins-game") },
                    { label: "Selected Projects", action: () => goToPathTop("/games") },
                    {
                        label: "Wishlist on Steam",
                        action: () => window.open(STEAM_JOURNEY_TO_ECHO, "_blank", "noopener,noreferrer"),
                    },
                ],
            },
            {
                title: "Studio",
                links: [
                    { label: "Production", action: () => scrollToId("about") },
                    { label: "Unity Development", action: () => scrollToId("about") },
                    { label: "Worldbuilding", action: () => scrollToId("about") },
                ],
            },
            {
                title: "Social",
                links: [
                    { label: "Discord", action: () => window.open(DISCORD_URL, "_blank", "noopener,noreferrer") },
                    { label: "Instagram", action: () => window.open(INSTAGRAM_URL, "_blank", "noopener,noreferrer") },
                    { label: "Website", action: () => window.open(WEBSITE_URL, "_blank", "noopener,noreferrer") },
                ],
            },
        ],
        [goToPathTop, scrollToId]
    );

    const socials = useMemo(
        () => [
            { label: "Discord", href: DISCORD_URL, Icon: DiscordIcon },
            { label: "Instagram", href: INSTAGRAM_URL, Icon: InstagramIcon },
            { label: "Website", href: WEBSITE_URL, Icon: GlobeIcon },
            { label: "Steam", href: STEAM_JOURNEY_TO_ECHO, Icon: SteamIcon },
        ],
        []
    );

    const bottomLinks = useMemo(
        () => [
            { label: "Home", action: () => goToPathTop("/") },
            { label: "Games", action: () => goToPathTop("/games") },
            { label: "About", action: () => scrollToId("about") },
        ],
        [goToPathTop, scrollToId]
    );

    return (
        <footer className="relative overflow-hidden border-t border-white/10 bg-black text-[#F3E8D6]">
            <div className="relative h-[170px] overflow-hidden border-b border-white/10 bg-black sm:h-[250px] lg:h-[330px]">
                <div className="pointer-events-none absolute inset-0">
                    <div className="absolute left-1/2 top-[-120px] h-[360px] w-[520px] -translate-x-1/2 bg-white/10 blur-[120px]" />
                    <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black to-transparent" />
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_35%,rgba(0,0,0,0.55)_100%)]" />
                </div>

                <div className="absolute bottom-[-12px] left-1/2 -translate-x-1/2 select-none">
                    <p className="text-[54px] font-black uppercase leading-none tracking-[0.04em] text-white/[0.08] sm:text-[120px] lg:text-[190px] xl:text-[230px]">
                        Northruins
                    </p>
                </div>
            </div>

            <div className="mx-auto max-w-[1500px] px-5 py-10 sm:px-8 sm:py-12 lg:px-12 lg:py-16">
                <div className="grid gap-10 lg:grid-cols-[0.95fr_1.55fr] lg:gap-16">
                    <div>
                        <button
                            type="button"
                            onClick={() => goToPathTop("/")}
                            className="group text-left"
                        >
                            <p className="text-[26px] font-black uppercase tracking-[0.15em] text-white transition group-hover:text-[#F3E8D6] sm:text-[34px]">
                                Northruins
                            </p>

                            <p className="mt-3 text-[9px] font-bold uppercase tracking-[0.3em] text-[#B48B57] sm:text-[10px]">
                                Game Development Studio
                            </p>
                        </button>

                        <p className="mt-6 max-w-[430px] text-[13px] leading-7 text-white/55 sm:text-[14px]">
                            Atmospheric survival games, readable systems and cinematic
                            presentation. Currently developing{" "}
                            <span className="text-[#F3E8D6]">Journey to Echo</span>.
                        </p>

                        <a
                            href={STEAM_JOURNEY_TO_ECHO}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-6 inline-flex w-full items-center justify-center gap-3 border border-white/15 bg-white px-5 py-3 text-[11px] font-black uppercase tracking-[0.14em] text-black transition hover:bg-[#F3E8D6] sm:w-auto"
                        >
                            <SteamIcon className="h-4 w-4" />
                            Add to Wishlist
                        </a>

                        <div className="mt-7 flex flex-wrap items-center gap-3">
                            {socials.map((social) => {
                                const Icon = social.Icon;

                                return (
                                    <a
                                        key={social.label}
                                        href={social.href}
                                        target="_blank"
                                        rel="noreferrer"
                                        aria-label={social.label}
                                        className="group flex h-10 w-10 items-center justify-center border border-white/10 bg-white/[0.03] text-white/70 transition duration-300 hover:-translate-y-1 hover:border-[#B48B57] hover:text-white sm:h-9 sm:w-9"
                                    >
                                        <Icon className="h-3.5 w-3.5 transition group-hover:scale-110" />
                                    </a>
                                );
                            })}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
                        {linkGroups.map((group) => (
                            <div key={group.title}>
                                <p className="text-[13px] font-semibold text-white">
                                    {group.title}
                                </p>

                                <ul className="mt-5 space-y-3.5 sm:mt-6 sm:space-y-4">
                                    {group.links.map((link) => (
                                        <li key={link.label}>
                                            <button
                                                type="button"
                                                onClick={link.action}
                                                className="group relative text-left text-[13px] leading-6 text-white/45 transition hover:text-white"
                                            >
                                                {link.label}
                                                <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#B48B57] transition-all duration-300 group-hover:w-full" />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="mt-12 flex flex-col gap-4 border-t border-white/10 pt-6 text-[12px] text-white/35 sm:mt-14 sm:flex-row sm:items-center sm:justify-between sm:pt-7">
                    <p>© {year} Northruins. All rights reserved.</p>

                    <div className="flex flex-wrap gap-x-5 gap-y-2">
                        {bottomLinks.map((item) => (
                            <button
                                key={item.label}
                                type="button"
                                onClick={item.action}
                                className="transition hover:text-white"
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default React.memo(FooterComponent);
